// Landing-page blog section — two-up grid surfacing the most recent posts.
// Pulls from the same `posts.ts` source-of-truth that powers /blog/.
import { Link } from "react-router";

import { AuthorAvatar } from "../blog/blog-index/AuthorAvatar";
import { BlogCover } from "../blog/blog-index/BlogCover";
import { POSTS, type BlogPost } from "../blog/blog-index/posts";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function formatDate(iso: string) {
  // ISO is YYYY-MM-DD — render as "April 30, 2026" without timezone surprises.
  const [y, m, d] = iso.split("-").map(Number);
  return `${MONTHS[m - 1]} ${d}, ${y}`;
}

const Arrow = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path
      d="M5 12h14M13 5l7 7-7 7"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function normalizeInternalPath(href: string) {
  return href.replace(/\/$/, "") || "/";
}

export function BlogPreview() {
  // Newest two — date-sorted descending.
  const recent = [...POSTS]
    .sort((a, b) => b.date.localeCompare(a.date))
    .slice(0, 2);

  return (
    <section
      id="blog"
      className="bg-[#FDFDFF] px-[clamp(20px,5vw,64px)] py-[clamp(72px,9vw,112px)]"
    >
      <div className="mx-auto max-w-[1200px]">
        {/* Header row */}
        <div className="mb-10 flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="flex items-center gap-3">
              <div className="h-px w-8 bg-indigo-300" />
              <div className="font-body text-xs font-semibold uppercase tracking-[0.14em] text-indigo-700">
                From the blog
              </div>
            </div>
            <h2 className="font-companion mt-3 max-w-[560px] text-[clamp(28px,3.6vw,36px)] font-bold leading-[1.15] tracking-[-0.025em] text-primary [text-wrap:balance]">
              Notes from the team.
            </h2>
          </div>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-600 no-underline"
          >
            All posts
            <Arrow />
          </Link>
        </div>

        {/* 2-up grid */}
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-7">
          {recent.map((post) => (
            <TwoUpCard key={post.id} post={post} />
          ))}
        </div>
      </div>
    </section>
  );
}

type TwoUpCardProps = {
  post: BlogPost;
};

const cardClassName =
  "two-up-card group flex flex-col rounded-[20px] border border-grindigo-100 bg-white p-6 no-underline shadow-[0_20px_40px_-24px_rgba(99,102,241,0.18)] transition-[transform,box-shadow,border-color] duration-[250ms] ease-[ease] hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-[0_28px_48px_-24px_rgba(99,102,241,0.28)]";

function TwoUpCard({ post }: TwoUpCardProps) {
  const body = <CardBody post={post} />;

  if (!post.href) {
    return <div className={cardClassName}>{body}</div>;
  }

  return (
    <Link to={normalizeInternalPath(post.href)} className={cardClassName}>
      {body}
    </Link>
  );
}

function CardBody({ post }: TwoUpCardProps) {
  return (
    <>
      <BlogCover
        tint={post.tint}
        mascot={post.mascot}
        height={220}
        imageSrc={post.coverImageSrc}
        imageSrcMobile={post.coverImageSrcMobile}
        imagePosition={post.coverImagePosition}
        imageAlt={post.title}
        coverArt={post.coverArt}
      />

      <div className="flex flex-grow flex-col px-1 pt-5">
        {/* Meta row */}
        <div className="mb-3.5 flex items-center gap-2.5 text-[11px] font-medium uppercase tracking-[0.06em] text-grindigo-500">
          <span className="font-semibold text-indigo-700">{post.tag}</span>
          <span
            aria-hidden="true"
            className="h-[3px] w-[3px] rounded-full bg-grindigo-300"
          />
          <span>{formatDate(post.date)}</span>
        </div>

        {/* Title */}
        <h3 className="font-companion mb-3.5 text-[clamp(22px,2.4vw,28px)] font-semibold leading-[1.15] tracking-[-0.02em] text-primary [text-wrap:balance]">
          {post.title}
        </h3>

        {/* Dek */}
        <p className="mb-5 flex-grow text-[15px] leading-[1.55] text-secondary [text-wrap:pretty]">
          {post.dek}
        </p>

        {/* Byline + Read */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <AuthorAvatar author={post.author} mascot={post.mascot} size={28} />
            <span className="text-[13px] text-secondary">
              <span className="font-semibold text-primary">
                {post.author.name}
              </span>
              {post.author.role ? ` · ${post.author.role}` : ""}
            </span>
          </div>
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-600">
            Read
            <Arrow size={14} />
          </span>
        </div>
      </div>
    </>
  );
}
